// featureWindowSurface — the ONE place that decides which feature-tree
// "surface" (a top-level tree parent + its child window items) is active for
// the current navigation state.
//
// Every domain routes through this resolver so FeatureTreeSidebar (which
// content to mount), FeatureWindowHost (which window to float in the
// viewport), and TopToolbar (when to auto-open the first window) all agree on
// the same surface — there is no second place that re-derives this
// classification and could drift from it.
//
// Two kinds of surface:
//   - FIXED: one registry entry per (vehicleTab, vehicleSubTab) pair, e.g.
//     'space/rocket-nozzle'. Used where the content is domain-specific.
//   - PRIMITIVE-DRIVEN: `primitive/<kind>`, where <kind> is the current
//     ImplicitPrimitive. Four surfaces share this shape — the Implicit
//     Primitives library, NASA Lattice Primitives, Land → Wheels, and
//     Space → Heat Exchanger — because all four ultimately render the SAME
//     `ImplicitPrimitivesContent` component keyed off `implicitPrimitive`
//     (each of Wheels/Heat-Exchanger locks that state to its one shape via its
//     TopToolbar dropdown, so this reduces to "one active primitive, wrapped
//     in a window titled after that primitive's own display name").
//
// Manual CAD and the plain Text-or-Image-to-CAD prompt box are NOT surfaces —
// they're handled one level up (isManualCad short-circuit, and returning
// `null` here for the untouched vehicleSubTab === '' case respectively) and
// keep rendering directly in the feature tree, unchanged.

import type { ImplicitPrimitive } from '@/config/implicitPrimitives';
import { IMPLICIT_PRIMITIVE_LABELS } from '@/config/implicitPrimitives';

export interface FeatureWindowSurfaceInput {
  vehicleEnabled: boolean;
  vehicleTab: string;
  vehicleSubTab: string;
  textToCadSelected: boolean;
  implicitPrimitive: ImplicitPrimitive;
}

export interface FeatureWindowSurface {
  /** Registry lookup key — see FEATURE_WINDOW_REGISTRY in featureWindowRegistry.ts. */
  key: string;
  /** The tree's top-level parent row label (the feature's own name). */
  parentLabel: string;
}

const primitiveSurface = (kind: ImplicitPrimitive): FeatureWindowSurface => ({
  key: `primitive/${kind}`,
  parentLabel: IMPLICIT_PRIMITIVE_LABELS[kind],
});

export function resolveFeatureWindowSurface({
  vehicleEnabled,
  vehicleTab,
  vehicleSubTab,
  textToCadSelected,
  implicitPrimitive,
}: FeatureWindowSurfaceInput): FeatureWindowSurface | null {
  if (vehicleEnabled) {
    if (vehicleTab === 'space') {
      if (vehicleSubTab === 'rocket-nozzle') {
        return {
          key: 'space/rocket-nozzle',
          parentLabel: 'Converging-Diverging De Laval Rocket Nozzle',
        };
      }
      if (vehicleSubTab === 'heat-exchanger') {
        return primitiveSurface(implicitPrimitive);
      }
      return null; // space sub-tabs without windows
    }
    if (vehicleTab === 'land') {
      if (vehicleSubTab === 'tubeframe-joint') {
        return { key: 'land/tubeframe-joint', parentLabel: 'Tubeframe Joint' };
      }
      if (vehicleSubTab === 'spaceframe') {
        return { key: 'land/spaceframe', parentLabel: 'Spaceframe Chassis' };
      }
      if (vehicleSubTab === 'cross-kart') {
        return {
          key: 'land/cross-kart',
          parentLabel: 'Cross Kart (2 Seater)',
        };
      }
      if (vehicleSubTab === 'wheels') {
        return primitiveSurface(implicitPrimitive);
      }
      // suspension-kinematics: JAWS owns its own tree via jawsSlot, not this
      // mechanism. Falls through to null.
      return null;
    }
    if (vehicleTab === 'air') {
      if (vehicleSubTab === 'nasa-supersonic-inlet') {
        return {
          key: 'air/nasa-supersonic-inlet',
          parentLabel: 'NASA SUPIN Supersonic Inlet Designer',
        };
      }
      return null; // airframe (disabled)
    }
    return null; // sea/hull (disabled)
  }

  if (textToCadSelected) {
    if (
      vehicleSubTab === 'implicit-primitives' ||
      vehicleSubTab === 'nasa-lattice-primitives'
    ) {
      return primitiveSurface(implicitPrimitive);
    }
    if (vehicleSubTab === 'nasa-unitcell-converter') {
      return {
        key: 'default/nasa-unitcell-converter',
        parentLabel: IMPLICIT_PRIMITIVE_LABELS['lattice-fill'],
      };
    }
    return null; // the plain Text-or-Image-to-CAD prompt box
  }

  return null;
}
